import { Card, Collapse, Input, Tag, Button, Popover, Space, Spin } from "antd";
import styles from "../index.module.less";
import { useParkingStatusCard } from "./index.hook";
import { ParkingStatusConstant } from "../../../constants/DatabaseConstants/ParkingConstants";
import Loading from "../../common/Loading";

import type { KeyFobDetails, MergedData } from "../../../shared/interface/dashboard.interface";

const { Panel } = Collapse;

interface ParkingStatusCardProps {
  data: MergedData[];
  loading: boolean;
  isFullScreen?: boolean;
}

type Config = {
  loading: boolean;
  keyFobDetails?: KeyFobDetails;
};

const ParkingStatusCard = (props: ParkingStatusCardProps) => {
  const config = useParkingStatusCard(props);

  const renderKeyFobDetails = (isUseSearchComponent: boolean = false) => {
    const { loading, keyFobDetails } = config as Config;

    if (loading) {
      return (
        <div className={styles["popover-loading"]}>
          <Space direction="vertical" className={styles["space--width"]} size="middle">
            <Spin tip="Loading" size="default" />
          </Space>
        </div>
      );
    } else if (keyFobDetails?.message) {
      return <div className={styles["text-red"]}>{keyFobDetails.message}</div>;
    } else if (keyFobDetails) {
      const { key_fob_token, username, mobileNumber, vehicleNumber, lastParkedOn } = keyFobDetails;

      const details = [isUseSearchComponent ? { label: "KeyFob Token", value: key_fob_token } : null, { label: "Username", value: username }, { label: "Mobile Number", value: mobileNumber }, { label: "Vehicle Number", value: vehicleNumber }, { label: "Last Parked On", value: lastParkedOn }].filter(Boolean) as Array<{ label: string; value?: string }>;

      return (
        <div className={styles["details-container"]}>
          {details.map(({ label, value }, index) => (
            <div className={styles["detail-row"]} key={index}>
              <span className={styles["label"]}>{label}:</span>
              <span className={styles["value"]}>{value || ""}</span>
            </div>
          ))}
        </div>
      );
    } else {
      return null;
    }
  };

  const renderUI = () => (
    <>
      <div className={styles["parking-card-header"]}>
        <Button size="small" className="gx-mb-0" onClick={() => config.handleShowAll(props?.data?.length)}>
          Show ALL
        </Button>
        <span className={props?.isFullScreen ? styles["card-title-full-screen"] : styles["card-title"]}>Parking Status</span>
        <Input.Search size="small" className={`gx-mb-0 gx-mr-5 ${styles["search-component"]}`} value={config?.searchValue} allowClear placeholder="Search by KeyFob or vehicle number..." onChange={(e) => config.handleChangeSearch(e.target.value)} onSearch={config.handleSearchAndScroll} />
      </div>

      {props.loading ? (
        <Loading />
      ) : !props.loading && props?.data && props?.data?.length > 0 ? (
        <Collapse activeKey={config.activeKeys} onChange={config.handleChange} className={props?.isFullScreen ? styles["parking-card-section-full-screen"] : styles["parking-card-section"]}>
          {props?.data?.map((item, index) => (
            <Panel
              key={index + 1}
              header={<div className={styles["panel-header"]}>{item?.name ?? ""}</div>}
              extra={
                <div className={styles["card-header-section"]}>
                  <div>
                    <span className={styles["parked-status-color"]}>Parked</span>: {item?.total_parked_slots ?? 0}
                  </div>
                  <div>
                    <span className={styles["empty-status-color"]}>Empty</span>: {item?.total_empty_slots ?? 0}
                  </div>
                  <div>
                    <span className={styles["dead-status-color"]}>Dead</span>: {item?.total_dead_slots ?? 0}
                  </div>
                  <div>
                    <span className={styles["booked-status-color"]}>Booked</span>: {item?.total_booked_slots ?? 0}
                  </div>
                </div>
              }
            >
              <div className={props?.isFullScreen ? styles["parking-grid-full-screen"] : styles["parking-grid"]}>
                {item?.keyfobList?.map((record) => (
                  <Popover
                    key={record?.id}
                    content={<div className={styles["main-container"]}>{renderKeyFobDetails()}</div>}
                    title={
                      <div className={styles["parking-popover-header"]}>
                        <div className={styles["parking-popover-title"]}>{record?.keyFob}</div>
                        <div className={`${styles["close-icon"]} icon icon-close`} onClick={config.handleClose}></div>
                      </div>
                    }
                    trigger="click"
                    open={config.keyFobId === record?.id}
                    onOpenChange={(open) => config.handlePopoverOpenChange(record?.id, open)}
                  >
                    <Tag
                      data-keyfob-id={record?.id}
                      className={`${styles["parking-cell"]} ${config.keyFobId === record?.id ? styles["selected-cell"] : ""} ${record?.parkingStatusName === ParkingStatusConstant.EXECUTION ? styles["blinking-record-execution"] : ""}`}
                      color={record?.parkingStatusName === ParkingStatusConstant.PARKED ? "white" : ""}
                      style={{
                        backgroundColor: record?.parkingStatusName === ParkingStatusConstant.PARKED ? "green" : record?.parkingStatusName === ParkingStatusConstant.WAITING ? "#f4cc07" : "white"
                      }}
                    >
                      {record?.keyFob}
                    </Tag>
                  </Popover>
                ))}
              </div>
            </Panel>
          ))}
        </Collapse>
      ) : (
        <div className={styles["parking-card-section"]}>
          <svg width="100%">
            <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" className="h3">
              No data available!
            </text>
          </svg>
        </div>
      )}
    </>
  );

  return props?.isFullScreen ? (
    <div className="gx-text-center gx-p-2">{renderUI()}</div>
  ) : (
    <Card title={null} bodyStyle={{ padding: "15px" }} className={`gx-card ${styles["card-container"]}`}>
      {renderUI()}
    </Card>
  );
};

export default ParkingStatusCard;
